// import {theme} from 'components/theme'
import {ScaleLinear} from 'd3-scale'
import {CSSProperties, ReactNode} from 'react'
import {match} from 'ts-pattern'
import {Fmt} from 'shared/forecast'
import {StyleProps} from 'shared/ui'
import {Rnge} from '../range'
import {Tooltip, useSafeMantineTheme} from '@mantine/core'
import {isString} from 'remeda'
import {WrapIf} from '../ui/WrapIf'

export type XAxisLabelsProps = Pick<
  XMetricLabelProps,
  'scale' | 'format' | 'showTick' | 'styles' | 'classes' | 'style' | 'className'
> & {
  placement?: 'center-tick' | 'book-ends'
}

export const XAxisLabelsDiv = ({placement, style, className, ...props}: XAxisLabelsProps) => {
  const {colors} = useSafeMantineTheme()
  const [start, end] = props.scale.domain()
  const labelProps = {
    ...props,
    size: '0.85em',
    styles: {
      ...props.styles,
      number: {
        fill: colors.gray[4],
        fontWeight: 'normal',
        ...props?.styles?.number
      }
    },
    xPlace: placement == 'book-ends' ? 'inside' : 'middle'
  } satisfies Partial<XMetricLabelProps>

  return (
    <div style={style} className={`flex flex-col ${className ?? ''}`}>
      {/* one relative to take up space */}
      <XMetricLabelDiv className='relative' value={start!} {...labelProps} />
      {/* the other absolute */}
      <XMetricLabelDiv className='absolute' value={end!} {...labelProps} />
    </div>
  )
}

export type XMetricLabelProps = {
  value: number
  format?: string | ((value: number) => string)
  place?: 'above' | 'below'
  xPlace?: 'inside' | 'middle' | 'overhang'
  label?: string
  color?: string
  classes?: {text?: string; label?: string; number?: string}
  styles?: {text?: CSSProperties; label?: CSSProperties; number?: CSSProperties; tick?: CSSProperties}
  size?: string
  showTick?: boolean
  hideLabel?: boolean
  tip?: ReactNode
} & {scale: ScaleLinear<number, number>} & StyleProps

export const XMetricLabelDiv = ({
  value,
  format,
  scale,
  place = 'below',
  xPlace = 'overhang',
  label,
  className,
  style,
  classes,
  styles,
  color,
  size,
  showTick,
  hideLabel,
  tip
}: XMetricLabelProps) => {
  const {colors} = useSafeMantineTheme()
  const x = scale(value)
  const side =
    xPlace == 'middle' ? 'middle'
    : value < Rnge.middleOf(scale.domain()) ? 'left'
    : 'right'
  const overhang = xPlace == 'overhang' ? 18 : 0
  return (
    <div
      className={`flex ${className ?? ''}`}
      style={{flexDirection: place == 'above' ? 'column' : 'column-reverse', ...style}}>
      {!hideLabel && (
        <div>
          {/** ⬆️ outer div to allow the text div to be smaller than the width */}
          <WrapIf if={!!tip} wrap={c => <Tooltip label={tip}>{c}</Tooltip>}>
            <div
              className={`relative inline-block ${classes?.text ?? ''}`}
              style={{
                left: x,
                transform: `translateX(${match({xPlace, side})
                  .with({xPlace: 'middle'}, {side: 'middle'}, () => '-50%')
                  .with({side: 'right'}, _ => `calc(-100% + ${overhang}px)`)
                  .with({side: 'left'}, _ => `-${overhang}px`)
                  .exhaustive()})`,
                textAlign: match({xPlace, side})
                  .with({xPlace: 'middle'}, {side: 'middle'}, () => 'center' as const)
                  .with({side: 'right'}, _ => 'left' as const)
                  .with({side: 'left'}, _ => 'right' as const)
                  .exhaustive(),
                fontSize: size,
                color: color ?? colors.gray[4]
              }}>
              <span
                className={`fill-gray-700 text-[1.2em] font-extrabold font-numbers ${classes?.number}`}
                style={{color, ...styles?.number}}>
                {isString(format) || format == null ? Fmt.fmt(value, {fmt: format}) : format(value)}
              </span>
              {label && (
                <span className={`pl-1 fill-gray-500 ${classes?.label}`} style={{...styles?.label}}>
                  {label}
                </span>
              )}
            </div>
          </WrapIf>
        </div>
      )}
      {showTick && (
        <WrapIf if={!!tip} wrap={c => <Tooltip label={tip}>{c}</Tooltip>}>
          <div
            className='relative w-[4px] rounded-md'
            style={{
              left: x,
              height: hideLabel ? 9 : 7,
              transform: 'translateX(-50%)',
              backgroundColor: color ?? colors.gray[4],
              ...styles?.tick
            }}></div>
        </WrapIf>
      )}
    </div>
  )
}
